/**
 * 🧮 원가 가져오기 조각 — 매입 목록(품목 마스터 → 최근/평균 매입가 · 매입 이력) / 인포트렌드 가격표(현재 분기 검색)
 *
 * v2.234.0 에 원가분석표 안에 있던 것을 v2.235.0 에서 떼어 냈다 — 견적서 작성 창의 [💡 가격 참고] 창이 같은 두 조각을 쓴다.
 *   · addLabel  단추 글자 (원가분석표 「+ 표에 추가」 · 가격 참고 「이 원가로 제안 단가 계산」)
 *   · showQty   수량 칸 — 가격 참고 창은 수량이 견적 줄에 있으니 숨긴다
 *   · initialQuery 처음 찾을 글자(견적 줄의 품목명)
 * 둘 다 고른 것을 CostLine 한 줄로 돌려준다 — 받는 쪽이 표에 붙이든(원가분석표) 원가 단가만 꺼내 쓰든(가격 참고) 알아서.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fmtKrw, newLineKey, type CostLine } from '../../lib/cost-sheet-calc';
import { fetchRecentSupplier, listItems } from '../../lib/stock-api';
import type { ItemMasterRow, PurchaseHistoryRow } from '../../lib/types';
import { getPurchasesByItem } from '../../lib/purchase-api';
import { searchInfortrendParts, type InfortrendSearchRow } from '../../lib/infortrend-api';
import { NumCell, Tile } from './CostUi';

export interface PickerProps {
  onAdd: (line: CostLine) => void;
  addLabel?: string;
  showQty?: boolean;
  initialQuery?: string;
}

/** 찾는 글자를 띄어쓰기로 나눠 전부 들어 있는지 — 「HDD 16TB」 로 「EDS 16TB SAS HDD」 도 찾게 */
export function matchesAllTokens(haystack: string, query: string): boolean {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const hay = haystack.toLowerCase();
  return tokens.every((t) => hay.includes(t));
}

export function PurchasePicker({ onAdd, addLabel = '+ 표에 추가', showQty = true, initialQuery = '' }: PickerProps) {
  const [q, setQ] = useState(initialQuery);
  const [picked, setPicked] = useState<ItemMasterRow | null>(null);
  const [info, setInfo] = useState<{ supplier: string; recent: number; avg: number; count: number } | null>(null);
  const [history, setHistory] = useState<PurchaseHistoryRow[]>([]);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [qty, setQty] = useState(1);
  const [basis, setBasis] = useState<CostLine['basis']>('recent');
  const [manualPrice, setManualPrice] = useState(0);
  const [vendor, setVendor] = useState('');

  // 전체 품목 마스터 — 한 번 받아 캐시 (나라장터 BOM 과 같은 키 → 캐시 공유)
  const items = useQuery({ queryKey: ['stock', 'items', 'all-for-picker'], queryFn: () => listItems(), staleTime: 5 * 60_000 });
  const filtered = useMemo(() => {
    const rows = items.data ?? [];
    if (!q.trim()) return rows.slice(0, 30);
    return rows.filter((r) => matchesAllTokens(`${r.itemName} ${r.itemGroup ?? ''} ${r.spec ?? ''} ${r.itemCode}`, q)).slice(0, 40);
  }, [items.data, q]);

  const pick = async (row: ItemMasterRow): Promise<void> => {
    setPicked(row);
    setInfo(null);
    setHistory([]);
    setBasis('recent');
    setQty(1);
    setLoadingInfo(true);
    try {
      const [i, hAll] = await Promise.all([
        fetchRecentSupplier(row.itemCode).catch(() => null),
        getPurchasesByItem({ itemCode: row.itemCode, limit: 24 }).catch(() => [] as PurchaseHistoryRow[]),
      ]);
      // 단가 0 줄(반품·회수·단가 없이 들어온 줄)은 고를 값이 없으니 뒤로 — 단가 있는 줄을 앞에 최대 8개
      const priced = hAll.filter((x) => (x.unitPrice ?? 0) > 0);
      const h = (priced.length > 0 ? priced : hAll).slice(0, 8);
      // ⚠「최근 매입가」는 가장 최근 매입 줄의 단가라 0 일 수 있다(단가 없이 들어온 줄 · 10-09 dev EDS 16TB 실측).
      //   0 이면 이력에서 단가가 있는 가장 최근 줄을 대신 쓴다 — 그래도 없으면 평균으로.
      const recentPositive = i?.recentPrice || priced[0]?.unitPrice || 0;
      const avg = i?.avgPrice ?? 0;
      const recent = recentPositive || avg;
      setInfo(i ? { supplier: i.supplier, recent, avg, count: i.count } : { supplier: '', recent: 0, avg: 0, count: 0 });
      setHistory(h);
      setVendor(i?.supplier ?? row.vendor ?? '');
      setManualPrice(recent);
      if (!i || !i.count) setBasis('manual');
      else if (!recentPositive && avg > 0) setBasis('avg');
    } finally {
      setLoadingInfo(false);
    }
  };

  const add = (): void => {
    if (!picked) return;
    const hasStats = !!info && info.count > 0;
    onAdd({
      key: newLineKey(), source: 'purchase', itemCode: picked.itemCode, pn: null,
      name: picked.itemName, spec: picked.spec ?? '', vendor: vendor.trim(),
      qty, unitCost: basis === 'manual' ? manualPrice : basis === 'avg' ? (info?.avg ?? 0) : (info?.recent ?? 0),
      recent: hasStats ? info!.recent : null, avg: hasStats ? info!.avg : null,
      basis: hasStats ? basis : 'manual', listPrice: null, quarter: null, sellUnit: null, note: '',
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4">
      <div>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 품명 · 품목그룹 · 규격 · 코드 (띄어쓰기로 여러 글자)"
          className="ui-input w-full mb-2" />
        <div className="border border-slate-200 rounded max-h-[520px] overflow-y-auto">
          {items.isPending ? (
            <div className="px-3 py-6 text-sm text-slate-400 text-center">품목을 불러오는 중…</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-6 text-sm text-slate-400 text-center">찾는 품목이 없습니다 — 글자를 줄여 보거나, 마스터에 없으면 직접 적으세요</div>
          ) : filtered.map((r) => (
            <button key={r.itemCode} type="button" onClick={() => { void pick(r); }}
              className={`block w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-blue-50 ${picked?.itemCode === r.itemCode ? 'bg-blue-50' : ''}`}>
              <div className="flex items-baseline gap-2">
                <span className="font-semibold text-sm text-slate-800">{r.itemName}</span>
                {r.itemGroup && <span className="text-xs px-1 py-0.5 bg-slate-100 text-slate-600 rounded">{r.itemGroup}</span>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{r.spec || <span className="text-slate-300">규격 -</span>}<span className="font-mono text-slate-400 ml-2">{r.itemCode}</span></div>
            </button>
          ))}
        </div>
        {!q.trim() && (items.data?.length ?? 0) > 30 && <div className="text-xs text-slate-500 mt-1">앞 30개만 보입니다 — 글자를 적어 좁히세요 (전체 {items.data?.length}개)</div>}
      </div>

      <div>
        {!picked ? (
          <div className="border border-dashed border-slate-300 rounded p-8 text-center text-sm text-slate-400">왼쪽에서 품목을 고르면 매입가와 매입 이력이 여기 나옵니다</div>
        ) : (
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <div className="font-bold text-slate-800">{picked.itemName} <span className="font-mono text-xs text-slate-500 ml-1">{picked.itemCode}</span></div>
              <div className="text-xs text-slate-600 mt-0.5">{picked.spec || '규격 -'}</div>
            </div>
            {loadingInfo ? (
              <div className="text-sm text-slate-400 py-4 text-center">매입 내역을 읽는 중…</div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <Tile label="최근 매입가" hint={info?.count ? `${info.count}건` : '매입 없음'} value={fmtKrw(info?.recent ?? 0)} />
                  <Tile label="평균 매입가" value={fmtKrw(info?.avg ?? 0)} />
                  <Tile label="최근 매입처" value={info?.supplier || '—'} />
                </div>
                {history.length > 0 && (
                  <div className="border border-slate-200 rounded overflow-hidden">
                    <div className="bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600 border-b border-slate-200">매입 이력 (최근 {history.length}건) — 「이 단가로」를 누르면 그 단가를 씁니다</div>
                    <table className="w-full text-xs">
                      <thead><tr className="text-slate-500"><th className="px-2 py-1 text-left">일자</th><th className="px-2 py-1 text-left">매입처</th><th className="px-2 py-1 text-left">영업건</th><th className="px-2 py-1 text-right">수량</th><th className="px-2 py-1 text-right">단가</th><th className="px-1 py-1"></th></tr></thead>
                      <tbody>
                        {history.map((h) => (
                          <tr key={h.purchaseId} className="border-t border-slate-100">
                            <td className="px-2 py-1 whitespace-nowrap">{h.orderDate ?? h.actualInDate ?? '—'}</td>
                            <td className="px-2 py-1 truncate max-w-[9rem]" title={h.supplier ?? ''}>{h.supplier ?? '—'}</td>
                            <td className="px-2 py-1 truncate max-w-[10rem]" title={h.salesEndUser ?? ''}>{h.salesEndUser ?? h.salesId ?? '—'}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{h.orderQty}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{fmtKrw(h.unitPrice)}</td>
                            <td className="px-1 py-1 text-right">
                              <button type="button" onClick={() => { setBasis('manual'); setManualPrice(h.unitPrice ?? 0); if (h.supplier) setVendor(h.supplier); }}
                                className="btn btn-secondary btn-sm">이 단가로</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
                  <label className="ui-label md:col-span-2">매입처
                    <input value={vendor} onChange={(e) => setVendor(e.target.value)} className="ui-input mt-0.5 w-full" placeholder="매입 내역에서 자동 — 고칠 수 있음" />
                  </label>
                  <label className="ui-label">원가 기준
                    <select value={basis} onChange={(e) => setBasis(e.target.value as CostLine['basis'])} className="ui-input mt-0.5 w-full" disabled={!info?.count}>
                      <option value="recent">최근 매입가</option>
                      <option value="avg">평균 매입가</option>
                      <option value="manual">직접</option>
                    </select>
                  </label>
                  {showQty && (
                    <label className="ui-label">수량
                      <div className="mt-0.5"><NumCell value={qty} step="any" className="w-full" onChange={setQty} /></div>
                    </label>
                  )}
                  {(basis === 'manual' || !info?.count) && (
                    <label className="ui-label md:col-span-2">원가 단가 (직접)
                      <div className="mt-0.5"><NumCell value={manualPrice} step="any" className="w-full" onChange={setManualPrice} /></div>
                    </label>
                  )}
                </div>
                <div className="flex justify-end">
                  <button type="button" onClick={add} className="btn btn-primary">{addLabel}</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function InfortrendPicker({ onAdd, addLabel = '+ 표에 추가', showQty = true, initialQuery = '' }: PickerProps) {
  const [q, setQ] = useState(initialQuery);
  const [debounced, setDebounced] = useState(initialQuery.trim());
  const [picked, setPicked] = useState<InfortrendSearchRow | null>(null);
  const [qty, setQty] = useState(1);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const search = useQuery({
    queryKey: ['price-list', 'infortrend', 'search', debounced],
    queryFn: () => searchInfortrendParts(debounced, { limit: 60 }),
    enabled: debounced.length > 0,
    staleTime: 60_000,
  });
  const rows = search.data?.rows ?? [];

  const add = (): void => {
    if (!picked) return;
    onAdd({
      key: newLineKey(), source: 'infortrend', itemCode: null, pn: picked.pn,
      name: (picked.model || picked.pn || '').trim(), spec: picked.description ?? '', vendor: '인포트렌드',
      qty, unitCost: picked.supply ?? 0, recent: null, avg: null, basis: 'manual',
      listPrice: picked.retail, quarter: picked.quarter_label, sellUnit: null, note: '',
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-4">
      <div>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 모델 · P/N · 설명 (예: GS 3024, 8TB)"
          className="ui-input w-full mb-2" />
        <div className="border border-slate-200 rounded max-h-[520px] overflow-y-auto">
          {!debounced ? (
            <div className="px-3 py-6 text-sm text-slate-400 text-center">모델명이나 설명을 적으면 현재 분기 가격표에서 찾습니다</div>
          ) : search.isPending ? (
            <div className="px-3 py-6 text-sm text-slate-400 text-center">찾는 중…</div>
          ) : search.isError ? (
            <div className="px-3 py-6 text-sm text-red-600 text-center">{(search.error as Error).message}</div>
          ) : rows.length === 0 ? (
            <div className="px-3 py-6 text-sm text-slate-400 text-center">없습니다 — 다른 글자로 찾아보세요</div>
          ) : rows.map((r) => (
            <button key={r.id} type="button" onClick={() => { setPicked(r); setQty(1); }}
              className={`block w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-violet-50 ${picked?.id === r.id ? 'bg-violet-50' : ''}`}>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-semibold text-sm text-slate-800">{r.model || r.pn}</span>
                {r.cat && <span className="text-xs px-1 py-0.5 bg-slate-100 text-slate-600 rounded">{r.cat}</span>}
                {!r.is_current && <span className="text-xs px-1 py-0.5 bg-amber-100 text-amber-800 rounded">{r.quarter_label} (옛 분기)</span>}
                {r.status && r.status !== 'Current' && <span className="text-xs px-1 py-0.5 bg-red-100 text-red-700 rounded">{r.status}</span>}
                <span className="ml-auto text-sm tabular-nums text-slate-800">{fmtKrw(r.supply)}</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5 truncate" title={r.description ?? ''}>{r.pn && r.model ? <span className="font-mono mr-2">{r.pn}</span> : null}{r.description}</div>
            </button>
          ))}
        </div>
        {search.data?.searchedAll && rows.length > 0 && <div className="text-xs text-amber-700 mt-1">현재 분기에 없어 옛 분기까지 찾았습니다 — 단종·가격 변동에 주의</div>}
      </div>
      <div>
        {!picked ? (
          <div className="border border-dashed border-slate-300 rounded p-8 text-center text-sm text-slate-400">왼쪽에서 품목을 고르세요</div>
        ) : (
          <div className="space-y-3">
            <div className="bg-violet-50 border border-violet-200 rounded p-3">
              <div className="font-bold text-slate-800">{picked.model || picked.pn}</div>
              <div className="text-xs text-slate-600 mt-0.5 font-mono">{picked.pn}</div>
              <div className="text-xs text-slate-600 mt-1">{picked.description}</div>
              <div className="text-xs text-slate-500 mt-1">{picked.quarter_label} 가격표{picked.is_current ? ' (현재 분기)' : ' (옛 분기)'}{picked.status ? ` · ${picked.status}` : ''}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Tile label="공급가 (원가로)" value={fmtKrw(picked.supply)} strong />
              <Tile label="소비자가 (참고)" value={fmtKrw(picked.retail)} />
            </div>
            {showQty && (
              <label className="ui-label block">수량
                <div className="mt-0.5"><NumCell value={qty} step="any" className="w-32" onChange={setQty} /></div>
              </label>
            )}
            <div className="flex justify-end">
              <button type="button" onClick={add} className="btn btn-primary">{addLabel}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
